import { jsPDF } from 'jspdf';
import { drawYourTourHeader, drawYourTourFooter } from './yourTourPdfBranding';

const NAVY  = [13, 34, 64];     // #0D2240
const PB    = [26, 74, 138];    // #1A4A8A
const AMBER = [232, 160, 32];   // #E8A020
const GRAY  = [107, 114, 128];  // #6B7280
const LGRAY = [229, 231, 235];  // #E5E7EB
const BLACK = [17, 24, 39];     // #111827
const WHITE = [255, 255, 255];

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

function fmt(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function setColor(doc, rgb) {
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}

function setFill(doc, rgb) {
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
}

function setDraw(doc, rgb) {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
}

// Returns the included categories for the quote letter "What's Included" section.
// Filters out internal-only rows (referral_fee, mst_staff_cost, davids_cost).
function getIncludedItems(breakdown) {
  return (breakdown || []).filter(
    (r) => !['referral_fee', 'mst_staff_cost', 'davids_cost'].includes(r.pricingType)
  );
}

/**
 * Generates a branded MST Sports Tours quote letter PDF.
 *
 * @param {object} request - Firestore request document data
 * @param {string|null} crestDataUrl - Club crest as data URL (optional)
 * @returns {string} base64-encoded PDF string
 */
export function generateQuoteLetterPdf(request, crestDataUrl = null, logoDataUrl = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const isYourTour = (request.brand || 'mst') === 'your_tour';
  const isPadelante = request.brand === 'padelante';
  const companyName = isYourTour ? 'Your Tour' : isPadelante ? 'Padelante' : 'MST Sports Tours';

  const pageW = 210;
  const pageH = 297;
  const marginL = 18;
  const marginR = 18;
  const contentW = pageW - marginL - marginR;

  const sym = CURRENCY_SYMBOLS[request.currency] || '£';
  const breakdown = request.quoteDetails?.breakdown || [];
  const included = getIncludedItems(breakdown);
  const costPerPerson = request.quoteDetails?.costPerPersonToCustomer || 0;

  // Spain → UK tours produce the quote letter in Spanish.
  const es = request.market === 'spain_to_uk';
  const L = es ? {
    tourQuote: 'Presupuesto del Tour', tourDetails: 'DETALLES DEL TOUR',
    sport: 'Deporte', destination: 'Destino', departure: 'Salida', return: 'Regreso',
    travellingParty: 'Grupo de viaje', groupType: 'Tipo de grupo',
    whatsIncluded: 'QUÉ INCLUYE', matchTickets: 'Entradas para partidos (Premier League)',
    priceTBC: 'Precio por confirmar — se emitirá una factura aparte una vez confirmado el partido',
    itinTBC: 'Itinerario completo por confirmar.', yourQuote: 'TU PRESUPUESTO',
    perPerson: 'por persona (según el grupo completo)', paymentSchedule: 'CALENDARIO DE PAGOS',
    deposit: 'Depósito', instalment2: '2º plazo', instalmentFinal: 'Plazo final',
    colPayment: 'Pago', colAmount: 'Importe', colDue: 'Fecha de vencimiento', nextSteps: 'PRÓXIMOS PASOS',
  } : {
    tourQuote: 'Tour Quote', tourDetails: 'TOUR DETAILS',
    sport: 'Sport', destination: 'Destination', departure: 'Departure', return: 'Return',
    travellingParty: 'Travelling party', groupType: 'Group type',
    whatsIncluded: "WHAT'S INCLUDED", matchTickets: 'Match Tickets (La Liga)',
    priceTBC: 'Price TBC — separate invoice to follow once fixture confirmed',
    itinTBC: 'Full itinerary to be confirmed.', yourQuote: 'YOUR QUOTE',
    perPerson: 'per person (based on full group travelling)', paymentSchedule: 'PAYMENT SCHEDULE',
    deposit: 'Deposit', instalment2: '2nd Instalment', instalmentFinal: 'Final Instalment',
    colPayment: 'Payment', colAmount: 'Amount', colDue: 'Due Date', nextSteps: 'NEXT STEPS',
  };

  let y = 0;

  // ── Header ─────────────────────────────────────────────────────────────────
  if (isYourTour) {
    y = drawYourTourHeader(doc, logoDataUrl, crestDataUrl, request.groupName || null);
  } else if (isPadelante && logoDataUrl) {
    // White header with the logo centred + divider — matches the invoice PDF
    const logoSize = 22;
    const logoX = (pageW - logoSize) / 2;
    const logoY = 10;
    let headerH = logoY + logoSize + 4;
    try {
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoSize, logoSize);
    } catch (_) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      setColor(doc, NAVY);
      doc.text(companyName, pageW / 2, 22, { align: 'center' });
      headerH = 30;
    }
    setDraw(doc, LGRAY);
    doc.setLineWidth(0.4);
    doc.line(marginL, headerH, pageW - marginR, headerH);
    y = headerH + 12;
  } else {
    setFill(doc, NAVY);
    doc.rect(0, 0, pageW, 38, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    setColor(doc, WHITE);
    doc.text(companyName, marginL, 17);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, [147, 197, 253]);
    doc.text(L.tourQuote, marginL, 25);

    setFill(doc, isPadelante ? [141, 198, 63] : AMBER);
    doc.rect(0, 35, pageW, 3, 'F');

    y = 50;
  }

  // ── Club crest (if provided) — Your Tour draws this inside its own header's
  // right panel (see drawYourTourHeader), so only MST/Padelante letters need
  // it placed here, below the header. ───────────────────────────────────────
  if (crestDataUrl && !isYourTour) {
    try {
      const crestSize = 22;
      const crestX = pageW - marginR - crestSize;
      doc.addImage(crestDataUrl, 'JPEG', crestX, y - 8, crestSize, crestSize);
    } catch (_) {
      // ignore crest errors — not critical
    }
  }

  // ── Intro greeting ─────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  setColor(doc, BLACK);

  const greeting = es
    ? `Estimado/a ${request.contactName || 'entrenador/a'},`
    : `Dear ${request.contactName || 'Coach'},`;
  doc.text(greeting, marginL, y);
  y += 7;

  const introOpener = isYourTour
    ? (es ? 'una experiencia Your Tour' : 'a Your Tour experience')
    : isPadelante
      ? (es ? 'una experiencia Padelante' : 'a Padelante experience')
      : (es ? 'un tour de MST Sports Tours' : 'an MST Sports Tour');
  const destText = request.destination || (es ? 'tu destino elegido' : 'your chosen destination');
  const introText = es
    ? `Gracias por tu interés en ${introOpener}. A continuación encontrarás tu presupuesto personalizado para tu próximo viaje a ${destText}. Estamos deseando ayudarte a ti y a tu grupo a vivir una experiencia inolvidable.`
    : `Thank you for your interest in ${introOpener}. Please find below your personalised quote for your upcoming trip to ${destText}. We look forward to helping you and your group have an unforgettable experience.`;
  const introLines = doc.splitTextToSize(introText, contentW);
  doc.setFontSize(10);
  setColor(doc, GRAY);
  doc.text(introLines, marginL, y);
  y += introLines.length * 5 + 6;

  // ── Divider ────────────────────────────────────────────────────────────────
  setDraw(doc, LGRAY);
  doc.setLineWidth(0.4);
  doc.line(marginL, y, pageW - marginR, y);
  y += 7;

  // ── Tour Details section ───────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.tourDetails, marginL, y);
  y += 5;

  const travellingParty = (Number(request.participants) || 0)
    + (Number(request.supportersMale) || 0) + (Number(request.supportersFemale) || 0)
    + (Number(request.customerStaff) || 0);

  const detailRows = [
    [L.sport, request.sport || '—'],
    [L.destination, request.destination || '—'],
    [L.departure, formatDate(request.departureDate) || '—'],
    [L.return, formatDate(request.returnDate) || '—'],
    // Travelling party = everyone the customer brings (players + supporters +
    // their own staff/teachers), which is the group the per-person price covers.
    [L.travellingParty, travellingParty ? `${travellingParty} ${es ? 'participantes' : 'participants'}` : '—'],
  ];
  if (request.groupType) detailRows.push([L.groupType, request.groupType]);

  const col1W = 50;
  detailRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(doc, GRAY);
    doc.text(label, marginL, y);
    setColor(doc, BLACK);
    doc.setFont('helvetica', 'bold');
    doc.text(value, marginL + col1W, y);
    y += 5.5;
  });

  y += 4;

  // ── Divider ────────────────────────────────────────────────────────────────
  setDraw(doc, LGRAY);
  doc.line(marginL, y, pageW - marginR, y);
  y += 7;

  // ── What's Included section ────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.whatsIncluded, marginL, y);
  y += 5;

  const hasMatchTickets = (request.selectedAddons || []).some((a) => a.categoryName === 'Match Tickets');

  if (included.length > 0 || hasMatchTickets) {
    included.forEach((item) => {
      // Bullet point
      setFill(doc, AMBER);
      doc.circle(marginL + 1.5, y - 1.5, 1.2, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setColor(doc, BLACK);
      doc.text(item.categoryName, marginL + 5, y);

      // Annotation / variant detail if available
      const annotation = request.addonAnnotations?.[item.categoryName];
      if (annotation) {
        doc.setFont('helvetica', 'normal');
        setColor(doc, GRAY);
        doc.setFontSize(9);
        doc.text(annotation, marginL + 5, y + 4);
        y += 4;
      }

      y += 5.5;
    });

    // Match Tickets always shown as TBC — never included in the main quote price
    if (hasMatchTickets) {
      setFill(doc, AMBER);
      doc.circle(marginL + 1.5, y - 1.5, 1.2, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      setColor(doc, BLACK);
      doc.text(L.matchTickets, marginL + 5, y);

      doc.setFont('helvetica', 'normal');
      setColor(doc, GRAY);
      doc.setFontSize(9);
      doc.text(L.priceTBC, marginL + 5, y + 4);
      y += 4 + 5.5;
    }
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(doc, GRAY);
    doc.text(L.itinTBC, marginL, y);
    y += 6;
  }

  y += 4;

  // ── Divider ────────────────────────────────────────────────────────────────
  setDraw(doc, LGRAY);
  doc.line(marginL, y, pageW - marginR, y);
  y += 7;

  // ── Pricing section ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.yourQuote, marginL, y);
  y += 5;

  // Per person highlight box
  const boxH = 22;
  setFill(doc, [235, 242, 252]); // blue-light tint
  setDraw(doc, [26, 74, 138]);   // PB border
  doc.setLineWidth(0.4);
  doc.roundedRect(marginL, y, contentW, boxH, 3, 3, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  setColor(doc, PB);
  doc.text(fmt(costPerPerson, sym), pageW / 2, y + 13, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.perPerson, pageW / 2, y + 18.5, { align: 'center' });

  y += boxH + 6;

  // Payment schedule calculation
  const payingPlayers = request.quoteDetails?.payingPlayers ?? (Number(request.participants) || 0);
  const totalCost = request.quoteDetails?.totalCostToCustomer ?? 0;
  const depositAmt = payingPlayers * 100;
  const remaining = Math.max(0, totalCost - depositAmt);
  const instalment2Amt = Math.round(remaining * 0.5 * 100) / 100;
  const instalment3Amt = Math.round((remaining - instalment2Amt) * 100) / 100;

  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }
  function subtractMonths(dateStr, months) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() - months);
    return d.toISOString().slice(0, 10);
  }
  function midDateStr(a, b) {
    if (!a || !b) return '';
    return new Date(Math.round((new Date(a).getTime() + new Date(b).getTime()) / 2)).toISOString().slice(0, 10);
  }

  const quoteDate = new Date().toISOString().slice(0, 10);
  const depositDueDate = addDays(quoteDate, 14);
  const finalDueDate = subtractMonths(request.departureDate, 2);
  const instalment2DueDate = midDateStr(depositDueDate, finalDueDate);

  if (depositAmt > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, GRAY);
    const depositLine = es
      ? `Para reservar tu plaza, se requiere un depósito de ${fmt(depositAmt, sym)} (${sym}100 por jugador) en un plazo de 2 semanas.`
      : `To secure your place, a deposit of ${fmt(depositAmt, sym)} (${sym}100 per paying player) is required within 2 weeks.`;
    const depositLines = doc.splitTextToSize(depositLine, contentW);
    doc.text(depositLines, marginL, y);
    y += depositLines.length * 4.5 + 5;
  }

  y += 2;

  // ── Divider ────────────────────────────────────────────────────────────────
  setDraw(doc, LGRAY);
  doc.line(marginL, y, pageW - marginR, y);
  y += 7;

  // ── Payment schedule ──────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.paymentSchedule, marginL, y);
  y += 5;

  const scheduleRows = [
    [L.deposit, fmt(depositAmt, sym), formatDate(depositDueDate)],
    [L.instalment2, fmt(instalment2Amt, sym), formatDate(instalment2DueDate)],
    [L.instalmentFinal, fmt(instalment3Amt, sym), formatDate(finalDueDate)],
  ];

  // Header row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  setColor(doc, GRAY);
  const col2 = marginL + 55;
  const col3 = marginL + 95;
  doc.text(L.colPayment, marginL, y);
  doc.text(L.colAmount, col2, y);
  doc.text(L.colDue, col3, y);
  y += 4;
  setDraw(doc, LGRAY);
  doc.setLineWidth(0.3);
  doc.line(marginL, y, pageW - marginR, y);
  y += 4;

  scheduleRows.forEach(([label, amount, dueDate]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, BLACK);
    doc.text(label, marginL, y);
    setColor(doc, PB);
    doc.setFont('helvetica', 'bold');
    doc.text(amount, col2, y);
    setColor(doc, BLACK);
    doc.setFont('helvetica', 'normal');
    doc.text(dueDate || '—', col3, y);
    y += 5.5;
  });

  y += 4;

  // ── La Liga match ticket note ──────────────────────────────────────────────
  if (hasMatchTickets) {
    setFill(doc, [255, 251, 235]); // amber-50
    setDraw(doc, [251, 191, 36]);  // amber-400
    doc.setLineWidth(0.4);
    const noteText = es
      ? 'Ten en cuenta: las entradas de la Premier League no están incluidas en el precio anterior. Una vez confirmado el partido (normalmente unas 2 semanas antes de tu llegada), se emitirá una factura aparte para las entradas, pagadera en un plazo de 7 días.'
      : 'Please note: La Liga match tickets are not included in the price above. Once the fixture has been confirmed (typically around 2 weeks before your arrival), a separate invoice will be issued for the match tickets, payable within 7 days.';
    const noteLines = doc.splitTextToSize(noteText, contentW - 10);
    const noteBoxH = noteLines.length * 4.5 + 10;
    doc.roundedRect(marginL, y, contentW, noteBoxH, 2, 2, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, [146, 64, 14]); // amber-800
    doc.text(noteLines, marginL + 5, y + 6);
    y += noteBoxH + 6;
  }

  // ── Divider ────────────────────────────────────────────────────────────────
  setDraw(doc, LGRAY);
  doc.line(marginL, y, pageW - marginR, y);
  y += 7;

  // ── Next steps ────────────────────────────────────────────────────────────
  // 72mm needed: section header + 4 steps + footer gap
  if (y > pageH - 72) {
    doc.addPage();
    y = 18;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(L.nextSteps, marginL, y);
  y += 5;

  const depositStepAmt = depositAmt > 0 ? fmt(depositAmt, sym) : `${sym}100 ${es ? 'por persona' : 'per person'}`;
  const steps = es ? [
    'Revisa este presupuesto con tu grupo y confirma el número de participantes.',
    'Firma y devuelve el formulario de reserva para asegurar tus fechas.',
    `Paga el depósito de ${depositStepAmt} antes del ${formatDate(depositDueDate)} para confirmar tu reserva.`,
    'A continuación empezaremos a organizar los partidos, el alojamiento y el transporte.',
  ] : [
    'Review this quote with your group and confirm numbers.',
    'Sign and return the booking form to secure your dates.',
    `Pay the deposit of ${depositStepAmt} by ${formatDate(depositDueDate)} to confirm your booking.`,
    'We will then begin arranging fixtures, accommodation, and transport.',
  ];

  steps.forEach((step, i) => {
    // Number badge
    setFill(doc, NAVY);
    doc.circle(marginL + 2, y - 1.5, 2.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    setColor(doc, WHITE);
    doc.text(String(i + 1), marginL + 2, y - 0.5, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, BLACK);
    const stepLines = doc.splitTextToSize(step, contentW - 8);
    doc.text(stepLines, marginL + 7, y);
    y += stepLines.length * 4.5 + 3;
  });

  y += 4;

  // ── Footer ─────────────────────────────────────────────────────────────────
  if (isYourTour) {
    drawYourTourFooter(doc, y, logoDataUrl);
  } else {
    const footerY = Math.max(y + 10, pageH - 28);
    setFill(doc, NAVY);
    doc.rect(0, footerY, pageW, pageH - footerY, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setColor(doc, WHITE);
    doc.text(companyName, marginL, footerY + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, [147, 197, 253]);
    doc.text('info@mstsportstours.com  ·  www.mstsportstours.com', marginL, footerY + 14);

    doc.setFontSize(7.5);
    setColor(doc, [156, 163, 175]);
    const disclaimer = es
      ? 'Este presupuesto es válido durante 30 días. Los precios están sujetos a disponibilidad y pueden cambiar sin previo aviso. Todos los precios incluyen IVA cuando corresponda.'
      : 'This quote is valid for 30 days. Prices are subject to availability and may change without notice. All prices include VAT where applicable.';
    const disclaimerLines = doc.splitTextToSize(disclaimer, contentW);
    doc.text(disclaimerLines, marginL, footerY + 20);
  }

  return doc.output('datauristring').split(',')[1];
}
